import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Globe2, RefreshCw, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(n || 0);

const STATUS_STYLES = {
  confirmed: 'bg-sage-50 text-sage-800 border-sage-200',
  checked_in: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  checked_out: 'bg-stone-100 text-stone-600 border-stone-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
};

const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-stone-200 rounded-md p-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-stone-500">{label}</p>
    <p className="text-2xl font-bold text-stone-900 font-['Manrope'] mt-1">{value}</p>
  </div>
);

const EMAIL_STYLES = {
  sent: ['Sent', 'text-emerald-700'],
  queued: ['Sending…', 'text-stone-500'],
  failed: ['Failed', 'text-red-600'],
  not_configured: ['Not set up', 'text-amber-700'],
};

const EmailStatus = ({ booking, onResend, sending }) => {
  const st = booking.confirmation_email?.status;
  const [label, cls] = EMAIL_STYLES[st] || ['—', 'text-stone-400'];
  return (
    <div className="flex items-center gap-1.5" data-testid={`email-status-${booking.id}`}>
      <span className={`text-xs font-medium flex items-center gap-1 ${cls}`} title={booking.confirmation_email?.error || ''}><Mail className="w-3.5 h-3.5" />{label}</span>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-stone-400 hover:text-sage-700" onClick={onResend} disabled={sending} title="Resend confirmation" data-testid={`resend-email-${booking.id}`}>
        <Send className={`w-3.5 h-3.5 ${sending ? 'animate-pulse' : ''}`} />
      </Button>
    </div>
  );
};

export const WebsiteBookingsTab = () => {
  const [data, setData] = useState({ bookings: [], stats: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/direct-bookings'); setData(res.data); }
    catch { toast.error('Failed to load website bookings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const [sendingId, setSendingId] = useState(null);
  const resend = async (b) => {
    setSendingId(b.id);
    try { await api.post(`/direct-bookings/${b.id}/send-confirmation`); toast.success(`Confirmation sent to ${b.guest_email}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Email failed'); load(); }
    finally { setSendingId(null); }
  };
  const { bookings, stats } = data;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Website Bookings" value={stats.total || 0} testId="stat-total" />
        <Stat label="Upcoming" value={stats.upcoming || 0} testId="stat-upcoming" />
        <Stat label="Revenue" value={fmt(stats.revenue)} testId="stat-revenue" />
        <Stat label="Cancelled" value={stats.cancelled || 0} testId="stat-cancelled" />
      </div>
      <div className="bg-white border border-stone-200 rounded-md">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <p className="font-semibold text-stone-800">Recent website bookings</p>
          <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-direct-bookings"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
        {bookings.length === 0 && !loading ? (
          <div className="p-12 text-center" data-testid="direct-bookings-empty">
            <Globe2 className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No website bookings yet. Create an API key and connect your website.</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Booking #</TableHead><TableHead>Guest</TableHead><TableHead>Room</TableHead>
              <TableHead>Stay</TableHead><TableHead>Meal Plan</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead>Email</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {bookings.map((b) => (
                <TableRow key={b.id} data-testid={`direct-booking-row-${b.id}`}>
                  <TableCell className="font-mono text-sm text-sage-700">{b.booking_number}</TableCell>
                  <TableCell><p className="font-medium text-stone-800">{b.guest_name}</p><p className="text-xs text-stone-400">{b.guest_email} · {b.guest_phone}</p></TableCell>
                  <TableCell>{b.room_number}</TableCell>
                  <TableCell className="text-sm">{b.check_in_date} → {b.check_out_date}</TableCell>
                  <TableCell className="text-sm capitalize">{(b.meal_plan || 'room_only').replace(/_/g, ' ')}</TableCell>
                  <TableCell className="text-right font-semibold">{fmt(b.total_amount)}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded border capitalize ${STATUS_STYLES[b.status] || STATUS_STYLES.confirmed}`}>{b.status?.replace('_', ' ')}</span></TableCell>
                  <TableCell><EmailStatus booking={b} onResend={() => resend(b)} sending={sendingId === b.id} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
